import { createApp } from "vue";
import { createPinia } from "pinia";
import { useAuthStore } from "@/stores/app/AuthStore";
import App from "./App.vue";
import { router } from "@/router";
import auth from "@/plugins/auth";
import "./assets/css/app.css";
import axios from "axios";
import preventEnterSubmit from "@/directives/v-prevent-enter-submit";
import { useMenuStore } from "@/stores/menu";
import { useAppStore } from "@/stores/app/AppStore";
import * as Sentry from "@sentry/vue";
import vue3GoogleLogin from "vue3-google-login";

const ENABLE_PWA = import.meta.env.VITE_ENABLE_PWA === "true";

// ✅ Register SW only if PWA is enabled
if (import.meta.env.PROD && ENABLE_PWA && "serviceWorker" in navigator) {
  const pwaModule = "virtual:pwa-register";
  // prevent Vite from trying to resolve at build time when plugin is absent
  import(/* @vite-ignore */ pwaModule).then(({ registerSW }) => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() { updateSW(true); },
      onOfflineReady() { console.log("✅ App ready offline"); },
    });
  });
}

// ✅ Clean up existing SW and caches if PWA disabled
if (import.meta.env.PROD && !ENABLE_PWA && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations?.().then((regs) =>
    regs.forEach((r) => r.unregister())
  );
  if ("caches" in window) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
}

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

const app = createApp(App);
app.use(createPinia());
app.use(router);
app.use(auth);
app.use(vue3GoogleLogin, {
  clientId:
    "557688370087-qqm96b6qkbjjj9a75fbsre6fdjovv3rf.apps.googleusercontent.com",
});

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/app\.summerjam\.com\.au/,
      /^https:\/\/staging\.summerjam\.com\.au/,
      /^https:\/\/api\.summerjam\.com\.au/,
      /^https:\/\/api-staging\.summerjam\.com\.au/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Ignore CSS preload errors (files exist, browser preload warnings are harmless)
      const errorMessage = event.message || '';
      const errorType = event.exception?.values?.[0]?.type || '';
      const errorValue = event.exception?.values?.[0]?.value || '';
      
      if (errorMessage.includes('Unable to preload CSS') || 
          errorMessage.includes('preload CSS for /assets/') ||
          errorType.includes('preload') ||
          errorValue.includes('preload CSS') ||
          errorValue.includes('Unable to preload')) {
        return null; // Don't send to Sentry
      }
      
      // Also check the original exception
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const errMsg = (error as any).message || '';
        if (errMsg.includes('Unable to preload CSS') || 
            errMsg.includes('preload CSS for /assets/')) {
          return null; // Don't send to Sentry
        }
      }
      
      // Ignore auth-related errors when token is invalid (expected behavior)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        const url = axiosError?.config?.url || axiosError?.request?.responseURL || '';
        const status = axiosError?.response?.status;
        
        // Ignore 401 errors from refresh endpoint (token expired/invalid - expected)
        if (url.includes('/api/auth/refresh') && status === 401) {
          return null; // Don't send to Sentry
        }
        
        // Ignore 500 errors from logout endpoint (token already invalid)
        if (url.includes('/api/auth/logout') && status === 500) {
          return null; // Don't send to Sentry
        }
        
        // Ignore JWT parsing errors from logout (token already invalid)
        if (url.includes('/api/auth/logout') && 
            (axiosError?.response?.data?.message?.includes('Token could not be parsed') ||
             axiosError?.message?.includes('Token could not be parsed'))) {
          return null; // Don't send to Sentry
        }
      }
      
      // Ignore network errors from auth endpoints
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = (error as any).message || '';
        const config = (error as any).config || {};
        const url = config?.url || '';
        
        // Ignore network errors from logout endpoint
        if (errorMessage.includes('Network Error') && url.includes('/api/auth/logout')) {
          return null; // Don't send to Sentry
        }
        
        // Ignore network errors from refresh endpoint
        if (errorMessage.includes('Network Error') && url.includes('/api/auth/refresh')) {
          return null; // Don't send to Sentry
        }
      }
      
      return event;
    },
  });
}

app.config.errorHandler = async (err, vm, info) => {
  console.error("Global error caught:", err);
  Sentry.captureException(err);
  const appStore = useAppStore();
  try {
    await appStore.checkLatestVersion();
  } catch (error) {
    appStore.refreshAndRedirectToHome(
      "There was an error. You will be redirected to the home page"
    );
  }
};

window.addEventListener("unhandledrejection", function (event) {
  const appStore = useAppStore();
  let reasonMessage = "";
  if (typeof event.reason === "string") {
    reasonMessage = event.reason;
  } else if (event.reason instanceof Error) {
    reasonMessage = event.reason.message;
  } else {
    console.warn(
      "Unhandled rejection reason is not a string or an Error:",
      event.reason
    );
  }
  if (reasonMessage.includes("Google 3P Authorization")) {
    appStore.googleLoginLibraryFailed = true;
  }
});

let hasAlerted = false;

window.addEventListener(
  "error",
  function (event) {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    
    const tagName = target.tagName;
    let assetPath: string | null = null;
    
    if (tagName === "SCRIPT") {
      assetPath = (target as HTMLScriptElement).src || null;
    } else if (tagName === "LINK") {
      assetPath = (target as HTMLLinkElement).href || null;
    }
    
    const isAssetsFolder = assetPath?.includes("/assets/");
    if ((tagName === "SCRIPT" || tagName === "LINK") && isAssetsFolder) {
      Sentry.captureException(
        new Error(
          `Old Asset detected (page reloading): ${assetPath}`
        )
      );
      if (!hasAlerted) {
        hasAlerted = true;
        alert("A new version of the app is available. Your page will refresh.");
        window.location.reload();
      }
    }
  },
  true
);

const initializeApp = async () => {
  const authStore = useAuthStore();
  const menuStore = useMenuStore();
  const appStore = useAppStore();

  authStore.init();

  router.beforeEach(async (to, from, next) => {
    // Handle activation route redirects BEFORE auth checks
    // If trying to access /activations/:slug with numeric ID or admin params, redirect to admin route
    if (to.name === 'public-activation') {
      const slug = to.params.slug as string
      const hasAdminParams = to.query.section || to.query.tab
      const isNumericId = /^\d+$/.test(slug)
      
      if (isNumericId || hasAdminParams) {
        // Redirect to admin route - now using /admin/activations/:id
        next({
          name: 'view-activation',
          params: { id: slug },
          query: to.query,
        })
        return
      }
    }

    // Check if route is public (doesn't require auth) before trying to refresh token
    const customAuth = to.meta?.custom_auth as any;
    const isPublicRoute = !customAuth?.required;
    const publicRouteNames = ['ticket-landing-page', 'ticket-widget', 'ticket-groups-page', 'ticketing-payment-return', 'lost-tickets-widget', 'public-activation', 'golden-jam-token'];
    const isKnownPublicRoute = publicRouteNames.includes(to.name as string);
    const isTicketingRoute = isPublicRoute || isKnownPublicRoute;
    
    if ((authStore as any).isAppReady == false) {
      // Only try to refresh token if route requires auth
      if (!isTicketingRoute) {
        try {
          await authStore.refreshToken();
          await authStore.getUserDetails();
          appStore.initialiseStore();
        } catch (error) {
          authStore.tokenValid = false;
        }
      } else {
        // For public/ticketing routes, don't initialize main app store (no auth needed)
        // Only initialize ticketing version check
        appStore.initialiseTicketingVersionCheck();
      }
    }
    await authStore.checkUserPermissions(to, from, next);
    (authStore as any).isAppReady = true;
  });

router.afterEach((to) => {
  // Close mobile menu after navigation
  menuStore.activeMobileMenu = false;

  const defaultTitle = 'Summer Jam Portal';
  const defaultDescription =
    'Summer Jam Championship ticketing, teams, and event management.';

  const title = (to.meta.title as string) || defaultTitle;
  const description = (to.meta.description as string) || defaultDescription;

  console.log(title);
  // ---- <title> ----
  document.title = title;

  // ---- <meta name="description"> ----
  let descTag = document.querySelector(
    'meta[name="description"]'
  ) as HTMLMetaElement | null;

  if (!descTag) {
    descTag = document.createElement('meta');
    descTag.name = 'description';
    document.head.appendChild(descTag);
  }
  descTag.content = description;

  // ---- <meta name="robots"> ----
  let robotsTag = document.querySelector(
    'meta[name="robots"]'
  ) as HTMLMetaElement | null;

  if (!robotsTag) {
    robotsTag = document.createElement('meta');
    robotsTag.name = 'robots';
    document.head.appendChild(robotsTag);
  }

  const hostname = window.location.hostname;

  const INDEXABLE_ROUTE_NAMES = ['ticket-landing-page'];

  // Allow robots "index, follow" on:
  // - tickets.summerjam.com.au   (real prod tickets domain)
  // - localhost                  (for testing only)
  const isTicketsHost =
    hostname === 'tickets.summerjam.com.au' ||
    hostname === 'localhost';
  const isWhitelistedRoute =
    typeof to.name === 'string' &&
    INDEXABLE_ROUTE_NAMES.includes(to.name);

const canIndex = isTicketsHost && isWhitelistedRoute;

robotsTag.content = canIndex ? 'index, follow' : 'noindex, nofollow';
});

  app.mount("#app");
  app.directive("prevent-enter-submit", preventEnterSubmit);
  
  // Set up visibility API to check version when tab comes into view
  // This works for both main app and ticketing routes
  let lastVisibilityCheck = 0;
  const VISIBILITY_CHECK_THROTTLE = 30000; // 30 seconds minimum between checks
  
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      // Throttle checks to avoid too many API calls
      if (now - lastVisibilityCheck > VISIBILITY_CHECK_THROTTLE) {
        lastVisibilityCheck = now;
        
        // Check if we're on a ticketing route
        const currentRoute = router.currentRoute.value;
        const publicRouteNames = ['ticket-landing-page', 'ticket-widget', 'ticket-groups-page', 'ticketing-payment-return', 'lost-tickets-widget'];
        const isTicketingRoute = publicRouteNames.includes(currentRoute.name as string);
        
        if (isTicketingRoute) {
          // Check ticketing version
          appStore.checkTicketingVersion().catch(() => {
            // Silently fail - version check is not critical
          });
        } else {
          // Check main app version (only if authenticated)
          if (authStore.tokenValid) {
            appStore.checkLatestVersion().catch(() => {
              // Silently fail - version check is not critical
            });
          }
        }
      }
    }
  });
};

initializeApp();